import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getProducts } from '../services/api';
import ProductCard from '../components/ProductCard';
import { FaSearch, FaFilter, FaTimes, FaTh, FaBars } from 'react-icons/fa';
import { toast } from 'react-toastify';
const ProductGallery = () => {
    const [products, setProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [priceRange, setPriceRange] = useState({ min: 0, max: 100 });
    const [sortBy, setSortBy] = useState('newest');
    const [viewMode, setViewMode] = useState('grid');
    const [showFilters, setShowFilters] = useState(false);
    const location = useLocation();

    const categories = ['All', 'Action Figures', 'Educational Toys', 'Soft Toys', 'Building Sets', 'Board Games'];
    const sortOptions = [
        { value: 'newest', label: 'Newest First' },
        { value: 'price-low', label: 'Price: Low to High' },
        { value: 'price-high', label: 'Price: High to Low' },
        { value: 'popular', label: 'Most Popular' }
    ];

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const category = params.get('category');
        if (category) {
            setSelectedCategory(category);
        }
        fetchProducts();
    }, [location]);

    const fetchProducts = async () => {
        try {
            const response = await getProducts();
            setProducts(response.products);
            setFilteredProducts(response.products);
        } catch (error) {
            console.error('Error fetching products:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        filterAndSortProducts();
    }, [searchTerm, selectedCategory, priceRange, sortBy, products]);

    const filterAndSortProducts = () => {
        let filtered = [...products];

        // Filter by search term
        if (searchTerm) {
            filtered = filtered.filter(product =>
                product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                product.description.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Filter by category
        if (selectedCategory && selectedCategory !== 'All') {
            filtered = filtered.filter(product => product.category === selectedCategory);
        }

        // Filter by price
        filtered = filtered.filter(product => 
            product.price >= priceRange.min && product.price <= priceRange.max
        );

        // Sort products
        switch (sortBy) {
            case 'price-low':
                filtered.sort((a, b) => a.price - b.price);
                break;
            case 'price-high':
                filtered.sort((a, b) => b.price - a.price);
                break;
            case 'popular':
                filtered.sort((a, b) => (b.popularity || 0) - (a.popularity || 0));
                break;
            default:
                filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        }

        setFilteredProducts(filtered);
    };

    const clearFilters = () => {
        setSearchTerm('');
        setSelectedCategory('');
        setPriceRange({ min: 0, max: 100 });
        setSortBy('newest');
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Hero Banner */}
            <div className="relative bg-gradient-to-r from-purple-600 to-pink-600 py-16">
                <div className="absolute inset-0 bg-black/20"></div>
                <div className="relative container mx-auto px-4 text-center text-white">
                    <h1 className="text-4xl md:text-5xl font-bold mb-4 animate-fade-in-up">
                        Our Toy Collection
                    </h1>
                    <p className="text-xl opacity-90 animate-fade-in-up animation-delay-200">
                        Discover the perfect toy for every child
                    </p>
                </div>
            </div>

            <div className="container mx-auto px-4 py-8">
                {/* Search and Filter Bar */}
                <div className="bg-white rounded-lg shadow-md p-4 mb-8 sticky top-16 z-40">
                    <div className="flex flex-col lg:flex-row gap-4">
                        {/* Search Box */}
                        <div className="flex-1 relative">
                            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search for toys..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                            />
                        </div>

                        {/* Category Filter */}
                        <select
                            value={selectedCategory}
                            onChange={(e) => setSelectedCategory(e.target.value)}
                            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        >
                            {categories.map(cat => (
                                <option key={cat} value={cat === 'All' ? '' : cat}>
                                    {cat}
                                </option>
                            ))}
                        </select>

                        {/* Sort By */}
                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        >
                            {sortOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>

                        {/* View Toggle */}
                        <div className="flex gap-2">
                            <button
                                onClick={() => setViewMode('grid')}
                                className={`p-2 rounded-lg transition ${viewMode === 'grid' ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-600'}`}
                            >
                                <FaTh />
                            </button>
                            <button
                                onClick={() => setViewMode('list')}
                                className={`p-2 rounded-lg transition ${viewMode === 'list' ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-600'}`}
                            >
                                <FaBars />
                            </button>
                        </div>

                        {/* Filter Toggle Button (Mobile) */}
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className="lg:hidden flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 rounded-lg"
                        >
                            <FaFilter /> Filters
                        </button>
                    </div>

                    {/* Price Filter */}
                    <div className={`mt-4 pt-4 border-t ${showFilters ? 'block' : 'hidden lg:block'}`}>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Price Range: £{priceRange.min} - £{priceRange.max}
                        </label>
                        <div className="flex gap-4">
                            <input
                                type="range"
                                min="0"
                                max="100"
                                value={priceRange.min}
                                onChange={(e) => setPriceRange({ ...priceRange, min: parseInt(e.target.value) })}
                                className="flex-1"
                            />
                            <input
                                type="range"
                                min="0"
                                max="100"
                                value={priceRange.max}
                                onChange={(e) => setPriceRange({ ...priceRange, max: parseInt(e.target.value) })}
                                className="flex-1"
                            />
                        </div>
                        <button
                            onClick={clearFilters}
                            className="mt-3 text-sm text-purple-600 hover:text-purple-700"
                        >
                            Clear All Filters
                        </button>
                    </div>
                </div>

                {/* Results Count */}
                <div className="mb-6 flex justify-between items-center">
                    <p className="text-gray-600">
                        Showing <span className="font-semibold">{filteredProducts.length}</span> of{' '}
                        <span className="font-semibold">{products.length}</span> products
                    </p>
                    {filteredProducts.length === 0 && (
                        <button onClick={clearFilters} className="text-purple-600 hover:text-purple-700">
                            Clear filters
                        </button>
                    )}
                </div>

                {/* Products Display */}
                {loading ? (
                    <div className="flex justify-center items-center h-96">
                        <div className="text-center">
                            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-600 mx-auto mb-4"></div>
                            <p className="text-gray-600">Loading amazing toys...</p>
                        </div>
                    </div>
                ) : filteredProducts.length === 0 ? (
                    <div className="text-center py-20 bg-white rounded-lg shadow-md">
                        <div className="text-6xl mb-4">🎁</div>
                        <h3 className="text-2xl font-semibold text-gray-800 mb-2">No products found</h3>
                        <p className="text-gray-600 mb-6">Try adjusting your filters or search term</p>
                        <button onClick={clearFilters} className="btn-primary">
                            Clear All Filters
                        </button>
                    </div>
                ) : (
                    <div className={viewMode === 'grid' 
                        ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
                        : "space-y-4"
                    }>
                        {filteredProducts.map((product, index) => (
                            <div key={product.id} className="animate-fade-in-up" style={{ animationDelay: `${index * 0.05}s` }}>
                                <ProductCard product={product} viewMode={viewMode} />
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProductGallery;